import { logger } from '@/lib/logger';
import { Download } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useTranslations } from 'next-intl';
import SimplePricingModal from '../modals/SimplePricingModal';

const WATERMARK_CONFIG = {
  fontSize: 0.025, // 水印字体大小比例
  minSize: 16, // 最小字体大小
  opacity: 0.6, // 水印不透明度
  padding: 0.015, // 水印边距比例
  text: 'anyvideo.ai', // 水印文本
};

// 生成友好的文件名
const generateFriendlyVideoName = (isWatermarked = false) => {
  const now = new Date();
  const dateStr = now.toISOString().slice(0, 10); // YYYY-MM-DD
  const timeStr = now.toTimeString().slice(0, 8).replace(/:/g, '-'); // HH-MM-SS

  const watermarkPrefix = isWatermarked ? 'watermarked-' : '';
  return `${watermarkPrefix}aiveo3ai-${dateStr}-${timeStr}.mp4`;
};

// 下载有水印版本
export const downloadVideo = async (videoUrl: string, watermarked: boolean) => {
  console.log('videoUrl', videoUrl);
  return new Promise((resolve, reject) => {
    fetch(videoUrl, {
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate, proxy-revalidate',
        Pragma: 'no-cache',
        Expires: '0',
      },
    })
      .then((res) => res.blob())
      .then((blob) => {
        const a = document.createElement('a');
        const objectUrl = window.URL.createObjectURL(blob);
        a.download = generateFriendlyVideoName(watermarked);
        a.href = objectUrl;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(objectUrl);
        a.remove();
        resolve(true);
        toast.success('video download started!');
      })
      .catch((error) => {
        reject(error);
        logger.error('Download error:', error);
        toast.error('Download failed. Please try right-clicking the video and select "Save as".');
      });
  });
};

// 修改下载处理函数
export const downloadImage = async (imageUrl: string, watermarked = false) => {
  try {
    // 先获取图片数据
    const response = await fetch(imageUrl, {
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate, proxy-revalidate',
        Pragma: 'no-cache',
        Expires: '0',
      },
    });
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    // 使用 Promise 包装图片加载
    await new Promise((resolve, reject) => {
      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;

        // 绘制图片
        ctx?.drawImage(img, 0, 0);

        // 如果需要添加水印

        if (ctx) {
          let watermarkSize = 0;
          if (watermarked) {
            // 添加水印
            watermarkSize = Math.max(
              canvas.width * WATERMARK_CONFIG.fontSize,
              WATERMARK_CONFIG.minSize,
            );
          }
          ctx.font = `${watermarkSize}px sans-serif`;
          ctx.fillStyle = `rgba(255, 255, 255, ${WATERMARK_CONFIG.opacity})`;
          ctx.strokeStyle = 'rgba(0, 0, 0, 0.3)';
          ctx.lineWidth = watermarkSize * 0.08;
          const padding = canvas.width * WATERMARK_CONFIG.padding;
          const text = WATERMARK_CONFIG.text;
          const textMetrics = ctx.measureText(text);
          ctx.strokeText(text, canvas.width - textMetrics.width - padding, canvas.height - padding);
          ctx.fillText(text, canvas.width - textMetrics.width - padding, canvas.height - padding);
        }

        // 下载图片
        const link = document.createElement('a');
        link.href = canvas.toDataURL('image/jpeg', 0.9);
        link.download = `aiveo3ai-image-${Date.now()}.jpg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // 清理 blob URL
        URL.revokeObjectURL(blobUrl);
        resolve(true);
      };

      img.onerror = () => {
        URL.revokeObjectURL(blobUrl);
        reject(new Error('Failed to load image'));
      };

      img.src = blobUrl;
    });
  } catch (error) {
    console.error('Download failed:', error);
    toast.error('Failed to download image');
  }
};

export const useDownloadOptions = () => {
  const [showDownloadOptions, setShowDownloadOptions] = useState(false);
  const [type, setType] = useState<'video' | 'image'>('video');
  const [url, setUrl] = useState('');
  const [watermarkUrl, setWatermarkUrl] = useState('');
  const [hasAccess, setHasAccess] = useState(false);
  const download = ({
    type,
    url,
    hasAccess,
    watermarkUrl,
  }: {
    type: 'video' | 'image';
    url?: string;
    hasAccess?: boolean;
    watermarkUrl?: string;
  }) => {
    console.log('🔧 DownloadOptions.download 被调用:', {
      type,
      url,
      hasAccess,
      watermarkUrl,
      '即将设置 showDownloadOptions': !hasAccess
    });
    
    setType(type);
    setUrl(url || '');
    setHasAccess(hasAccess || false);
    setWatermarkUrl(watermarkUrl || '');
    
    if (hasAccess) {
      console.log('✅ 用户有权限，直接下载');
      if (type === 'video') {
        downloadVideo(url || watermarkUrl || '', false);
      } else {
        downloadImage(url || watermarkUrl || '', false);
      }
    } else {
      console.log('❌ 用户无权限，显示下载选项弹窗');
      setShowDownloadOptions(true);
      
      // 立即触发重新渲染检查
      console.log('⚡ 立即设置 showDownloadOptions 为 true');
    }
  };

  return {
    showDownloadOptions,
    setShowDownloadOptions,
    download,
    type,
    url,
    hasAccess,
    watermarkUrl,
  };
};

export const DownloadOptions = ({
  showDownloadOptions,
  setShowDownloadOptions,
  type,
  watermarkUrl,
  url,
  hasAccess,
}: ReturnType<typeof useDownloadOptions>) => {
  const [showPricingModal, setShowPricingModal] = useState(false);
  const t = useTranslations('DownloadOptions');

  console.log('🎨 DownloadOptions 组件渲染:', {
    showDownloadOptions,
    type,
    hasAccess,
    url,
    watermarkUrl,
    '组件是否应该显示': showDownloadOptions
  });

  return (
    <>
      <SimplePricingModal isOpen={showPricingModal} onClose={() => setShowPricingModal(false)} />

      {showDownloadOptions && (
        <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/70 p-4 backdrop-blur-lg">
          {/* 电影感背景效果 */}
          <div className="absolute inset-0 bg-gradient-to-br from-black via-green-950/20 to-black" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(34,197,94,0.1),transparent_50%)]" />
          
          <div className="relative w-full max-w-sm rounded-2xl border border-green-500/30 bg-black/95 p-6 shadow-[0_0_50px_rgba(34,197,94,0.2)] backdrop-blur-xl sm:max-w-md sm:p-8">
            {/* 绿色装饰条 */}
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-transparent via-green-400 to-transparent" />
            
            {/* 右上角关闭按钮 */}
            <button
              onClick={() => setShowDownloadOptions(false)}
              className="absolute right-4 top-4 z-10 rounded-full border border-green-500/30 bg-black/60 p-2 text-gray-400 transition-all duration-300 hover:border-green-400/60 hover:bg-green-500/20 hover:text-green-300"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            {/* 标题区域 */}
            <div className="mb-6 text-center pr-8">
              <h3 className="mb-2 text-xl font-bold bg-gradient-to-r from-green-400 to-cyan-400 bg-clip-text text-transparent sm:text-2xl">
                {t('title')}
              </h3>
            </div>

            <div className="space-y-4 sm:space-y-5">
              {/* 有水印版本选项 - 电影感设计 */}
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('🎯 有水印按钮被点击，直接下载');
                  
                  if (type === 'video') {
                    downloadVideo(watermarkUrl || url, true);
                  } else {
                    downloadImage(watermarkUrl || url, true);
                  }
                  setShowDownloadOptions(false);
                }}
                className="group w-full rounded-xl border border-green-500/20 bg-gradient-to-r from-gray-900/80 to-gray-800/80 p-4 text-left transition-all duration-300 hover:border-green-400/40 hover:shadow-[0_0_20px_rgba(34,197,94,0.1)] sm:p-5"
              >
                <div className="flex items-start space-x-3 sm:space-x-4">
                  <div className="mt-1 rounded-lg bg-green-500/20 p-2">
                    <Download className="h-4 w-4 text-green-400 sm:h-5 sm:w-5" />
                  </div>
                  <div className="flex-1">
                    <h4 className="mb-1 text-sm font-semibold text-white group-hover:text-green-100 sm:text-base">
                      {t('withWatermark')}
                    </h4>
                    <p className="text-xs text-gray-500 group-hover:text-gray-400 sm:text-sm">
                      {t('free')}
                    </p>
                  </div>
                  <div className="mt-2 h-2 w-2 rounded-full bg-green-500 opacity-60 group-hover:opacity-100" />
                </div>
              </button>

              {/* 无水印版本选项 - 背景突出设计 */}
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('🎯 无水印按钮被点击，hasAccess:', hasAccess);
                  
                  if (!hasAccess) {
                    setShowDownloadOptions(false);
                    setShowPricingModal(true);
                    return;
                  }
                  if (type === 'video') {
                    downloadVideo(url, false);
                  } else {
                    downloadImage(url, false);
                  }
                  setShowDownloadOptions(false);
                }}
                className="group relative w-full rounded-xl border border-green-400/50 bg-gradient-to-r from-green-800/60 to-cyan-800/60 p-4 text-left transition-all duration-300 hover:border-green-400/70 hover:shadow-[0_0_20px_rgba(34,197,94,0.3)] sm:p-5"
              >
                {/* 背景突出效果 - 添加pointer-events-none防止点击拦截 */}
                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-green-500/20 to-cyan-500/20 opacity-40 transition-opacity duration-300 group-hover:opacity-60 pointer-events-none" />
                
                <div className="relative flex items-start space-x-3 sm:space-x-4">
                  <div className="mt-1 rounded-lg bg-gradient-to-br from-green-400/40 to-cyan-400/40 p-2 shadow-[0_0_10px_rgba(34,197,94,0.3)]">
                    <div className="h-4 w-4 rounded-full bg-gradient-to-r from-green-400 to-cyan-400 sm:h-5 sm:w-5" />
                  </div>
                  <div className="flex-1">
                    <h4 className="mb-1 text-sm font-semibold text-white group-hover:text-green-100 sm:text-base">
                      {t('withoutWatermark')}
                    </h4>
                    <p className="text-xs text-green-300/80 group-hover:text-green-200 sm:text-sm">
                      {t('premium')}
                    </p>
                  </div>
                  <div className="mt-2 h-2 w-2 rounded-full bg-gradient-to-r from-green-400 to-cyan-400 shadow-[0_0_10px_rgba(34,197,94,0.5)]" />
                </div>
              </button>
            </div>
            
            {/* 底部装饰效果 */}
            <div className="absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-green-500/5 to-transparent" />
          </div>
        </div>
      )}
    </>
  );
};
